import { toast } from 'sonner';
import i18next from 'i18next';

import { useTenantStore } from '@/store/tenant.store.js';
import { hydrationManager } from '@/lib/services/reference-hydration.js';
import { createListLocate } from '@/lib/notifications/create-list-locate.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';


/**
 * @param {import('@repo/sdk').SimpleCrudService} service
 * @param {import('zustand').UseBoundStore<any>} useStore
 * @param {{
 *   referenceEntity?: string,
 *   translationKeyPrefix?: string,
 *   messagesKeyPrefix?: string,
 *   messageKeys?: { create?: string, update?: string, delete?: string },
 *   getItems?: (state: any) => unknown[],
 * }} [options]
 */
export function createSimpleTableIntegration(service, useStore, options = {}) {
    const referenceEntity = options.referenceEntity;
    const translationKeyPrefix = options.translationKeyPrefix;
    /**
     * Full i18n key prefix for toast rule messages, e.g.
     * `kvkkSimpleDefinitions:dataDisposalMethod.messages` → `.createSuccess`.
     */
    const messagesKeyPrefix = options.messagesKeyPrefix;
    const messageKeys = {
        create: options.messageKeys?.create || 'createSuccess',
        update: options.messageKeys?.update || 'updateSuccess',
        delete: options.messageKeys?.delete || 'deleteSuccess',
    };
    const refreshReferenceEntity = () => {
        if (!referenceEntity) return;
        hydrationManager.refreshEntity(referenceEntity, { force: true });
    };

    const successToast = (kind, englishFallback) => {
        if (messagesKeyPrefix) {
            return i18next.t(`${messagesKeyPrefix}.${messageKeys[kind]}`, {
                defaultValue: englishFallback,
            });
        }
        if (translationKeyPrefix) {
            const suffix = kind === 'create' ? 'created' : kind === 'update' ? 'updated' : 'deleted';
            return i18next.t(`${translationKeyPrefix}.${suffix}`, {
                defaultValue: englishFallback,
            });
        }
        const integrationKey =
            kind === 'create'
                ? 'integrationToasts:recordCreated'
                : kind === 'update'
                    ? 'integrationToasts:recordUpdated'
                    : 'integrationToasts:recordDeleted';
        return i18next.t(integrationKey, { defaultValue: englishFallback });
    };

    const integration = {
        async fetchList(
            page = 1,
            pageSize = 10,
            search = '',
            sortBy = '',
            sortOrder = 'desc',
            columnFilters = {},
            extraQuery = {},
        ) {
            const store = useStore.getState();
            const tenantId = useTenantStore.getState().selectedTenantId;
            if (!tenantId) {
                store.setLoading(false);
                const msg = i18next.t('common:messages.tenantRequired', {
                    defaultValue: 'Tenant selection is required',
                });
                return { success: false, error: msg };
            }
            store.setLoading(true);
            store.clearError();
            try {
                const params = {
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    tenantId,
                    ...extraQuery,
                };
                if (columnFilters && typeof columnFilters === 'object') {
                    Object.entries(columnFilters).forEach(([k, v]) => {
                        if (v !== undefined && v !== null && v !== '') {
                            params[k] = Array.isArray(v) ? v.join(',') : v;
                        }
                    });
                }
                const res = await service.getAll(params);
                store.setItems(res.data || []);
                if (res.pagination) {
                    store.setPaginationData({
                        currentPage: res.pagination.page,
                        totalPages: res.pagination.totalPages,
                        totalItems: res.pagination.total,
                        itemsPerPage: res.pagination.pageSize,
                        hasNextPage: res.pagination.page < res.pagination.totalPages,
                        hasPreviousPage: res.pagination.page > 1,
                    });
                }
                if (res.uniqueValues) {
                    store.setUniqueValues(res.uniqueValues);
                }
                return { success: true };
            } catch (error) {
                const msg = getIntegrationErrorMessage(error, {
                    notFoundDefault: 'Record not found',
                }) || i18next.t('common:messages.loadFailed', {
                    defaultValue: 'Failed to load',
                });
                store.setError(msg);
                toast.error(msg);
                return { success: false, error: msg };
            } finally {
                store.setLoading(false);
            }
        },

        /** Lookup list without mutating the table store or toasting. */
        async queryAll(params = {}) {
            return service.getAll(params);
        },

        async createRecord(data) {
            try {
                const res = await service.create(data);
                toast.success(successToast('create', 'Record created'));
                refreshReferenceEntity();
                return { success: true, data: res.data };
            } catch (error) {
                const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotCreate', {
                    defaultValue: 'Could not create',
                });
                toast.error(msg);
                return { success: false, error: msg };
            }
        },

        async updateRecord(id, data) {
            try {
                const res = await service.update(id, data);
                toast.success(successToast('update', 'Record updated'));
                refreshReferenceEntity();
                return { success: true, data: res.data };
            } catch (error) {
                const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotUpdate', {
                    defaultValue: 'Could not update',
                });
                toast.error(msg);
                return { success: false, error: msg };
            }
        },

        async removeRecord(id) {
            try {
                const res = await service.delete(id);
                toast.success(successToast('delete', 'Record deleted'));
                refreshReferenceEntity();
                return { success: true, data: res.data };
            } catch (error) {
                const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotDelete', {
                    defaultValue: 'Could not delete',
                });
                toast.error(msg);
                return { success: false, error: msg };
            }
        },
    };

    // Notification deep-link: jump to the page containing the focused row.
    integration.locateRowInList = createListLocate({
        service,
        useStore,
        getItems: options.getItems || ((s) => s.items),
        fetchList: (...args) => integration.fetchList(...args),
    });

    return integration;
}
